/* eslint-disable @typescript-eslint/no-shadow */
import React, { FunctionComponent, useEffect, useState } from 'react';

import { useId } from '@radix-ui/react-id';
import clsx from 'clsx';
import { Controller, useForm } from 'react-hook-form';

import { WholesaleItem, WholesaleLoginRequest } from '@domains/login';

import { useCommonData } from '@stores/global-data';

import { apiWholesaleLogin } from '@api/methods';

import { VALIDATION_MESSAGES, VALIDATORS } from '@utils/constants';
import { formatRussianPhoneNumber } from '@utils/contacts';
import { makeInfoPageRoute } from '@utils/route';
import { applyTypograf } from '@utils/typograf';

import { Button } from '@components/common/Button/Button';
import { InvisibleCaptcha } from '@components/common/InvisibleCaptcha/InvisibleCaptcha';
import { useInvisibleCaptcha } from '@components/common/InvisibleCaptcha/use-invisible-captcha';
import { Link } from '@components/common/Link/Link';
import { StyledCheckbox } from '@components/common/StyledCheckbox/StyledCheckbox';
import { StyledInput } from '@components/common/StyledInput';
import { InnInput } from '@components/forms/InnInput/InnInput';
import { EyeFilledIcon } from '@components/icons';

import styles from './LoginDialogLegalLoginForm.module.scss';

const dataProcessingPolicyText1 = applyTypograf(
    'Я согласен на обработку персональных данных\nв соответствии с',
);
const dataProcessingPolicyText2 = applyTypograf(
    'Политикой в отношении обработки\nперсональных данных',
);
const userAgreementText1 = applyTypograf('Я принимаю условия');
const userAgreementText2 = applyTypograf('Пользовательского соглашения');

export type FormValues = {
    inn: string;
    password: string;
    hasAcceptedDataProcessingPolicy: boolean;
    hasAcceptedUserAgreement: boolean;
};

const defaultValues: FormValues = {
    inn: '',
    password: '',
    hasAcceptedDataProcessingPolicy: false,
    hasAcceptedUserAgreement: false,
};

export type LoginDialogLegalLoginFormProps = Readonly<{
    onSuccess: () => void;
}>;

export const LoginDialogLegalLoginForm: FunctionComponent<LoginDialogLegalLoginFormProps> = (
    props,
) => {
    const { onSuccess } = props;

    const dataProcessingPolicyCheckboxId = useId();
    const userAgreementCheckboxId = useId();

    const [isHiddenPassword, setIsHiddenPassword] = useState<boolean>(true);
    const [isFormSubmitting, setIsFormSubmitting] = useState(false);
    const [selectedWholesale, setSelectedWholesale] = useState<WholesaleItem | null>(null);

    const invisibleCaptcha = useInvisibleCaptcha();
    const { getCaptchaToken, isCaptchaLoading } = invisibleCaptcha;

    const { contacts } = useCommonData();
    const phone = contacts.support.phone;

    const form = useForm<FormValues>({
        defaultValues,
        shouldUnregister: true,
        mode: 'onSubmit',
    });

    const { handleSubmit, setValue, setError, formState, clearErrors, watch } = form;

    const isGeneralErrorShowing =
        formState.isSubmitted && formState.errors.password?.message === '' && !formState.isValid;

    const watchedInn = watch('inn');
    const watchedPassword = watch('password');

    useEffect(() => {
        setValue('password', '');
        setSelectedWholesale(null);
    }, [watchedInn, setValue]);

    useEffect(
        () => {
            if (isGeneralErrorShowing) {
                clearErrors();
            }
        },
        [watchedInn, watchedPassword, clearErrors], // eslint-disable-line react-hooks/exhaustive-deps,
    );

    const onSubmit = async (data: FormValues) => {
        if (isFormSubmitting) return;
        setIsFormSubmitting(true);

        let captchaToken: string | undefined;
        try {
            captchaToken = await getCaptchaToken();
        } catch {
            setIsFormSubmitting(false);
            return;
        }

        if (selectedWholesale === null) {
            return;
        }

        const { password } = data;

        const payload: WholesaleLoginRequest = {
            login: selectedWholesale.id,
            password,
            captcha_token: captchaToken,
        };

        try {
            await apiWholesaleLogin(payload);
            onSuccess();
        } catch {
            setError('inn', { message: '' });
            setError('password', { message: '' });
        } finally {
            setIsFormSubmitting(false);
        }
    };

    const togglePasswordVisibility = () => {
        setIsHiddenPassword((flag) => !flag);
    };

    const handleWholesaleSelect = (wholesale: WholesaleItem | null) => {
        setSelectedWholesale(wholesale);
    };

    return (
        <div className={styles.root}>
            <form onSubmit={handleSubmit(onSubmit)} className={styles.form}>
                <Controller
                    control={form.control}
                    name="inn"
                    rules={{
                        validate: VALIDATORS.innValidator,
                        required: VALIDATION_MESSAGES.required,
                    }}
                    render={({ field, fieldState }) => (
                        <InnInput
                            onWholesaleSelect={handleWholesaleSelect}
                            selectedWholesale={selectedWholesale}
                            errorMessage={fieldState.error?.message}
                            hasError={fieldState.error?.message !== undefined}
                            disabled={isFormSubmitting}
                            className={styles.innInput}
                            {...field}
                        />
                    )}
                />
                {selectedWholesale !== null && (
                    <Controller
                        control={form.control}
                        name="password"
                        rules={{ required: VALIDATION_MESSAGES.required }}
                        render={({ field, fieldState }) => (
                            <StyledInput
                                type={isHiddenPassword ? 'password' : 'text'}
                                label="Пароль"
                                actionIcon={EyeFilledIcon}
                                onActionButtonClick={togglePasswordVisibility}
                                className={styles.passwordInput}
                                classes={{
                                    actionButton: clsx(
                                        styles.actionButton,
                                        !isHiddenPassword && styles.isVisiblePassword,
                                    ),
                                }}
                                isKeepHintPlace
                                hintMessage={fieldState.error?.message}
                                hasError={fieldState.error?.message !== undefined}
                                disabled={isFormSubmitting}
                                {...field}
                            />
                        )}
                    />
                )}
                <Controller
                    control={form.control}
                    name="hasAcceptedDataProcessingPolicy"
                    rules={{
                        required: VALIDATION_MESSAGES.required,
                        validate: VALIDATORS.isChecked,
                    }}
                    render={({ field, fieldState, formState }) => (
                        <StyledCheckbox
                            id={dataProcessingPolicyCheckboxId}
                            className={styles.dataProcessingPolicyCheckbox}
                            hasError={fieldState.error !== undefined}
                            isDisabled={formState.isSubmitting}
                            {...field}
                        />
                    )}
                />
                <label
                    htmlFor={dataProcessingPolicyCheckboxId}
                    className={styles.dataProcessingPolicyCheckboxLabel}
                >
                    {dataProcessingPolicyText1}{' '}
                    <Link
                        route={makeInfoPageRoute('privacy')}
                        target="_blank"
                        className={styles.labelLink}
                    >
                        {dataProcessingPolicyText2}
                    </Link>
                </label>
                <Controller
                    control={form.control}
                    name="hasAcceptedUserAgreement"
                    rules={{
                        required: VALIDATION_MESSAGES.required,
                        validate: VALIDATORS.isChecked,
                    }}
                    render={({ field, fieldState, formState }) => (
                        <StyledCheckbox
                            id={userAgreementCheckboxId}
                            className={styles.userAgreementCheckbox}
                            hasError={fieldState.error !== undefined}
                            isDisabled={formState.isSubmitting}
                            {...field}
                        />
                    )}
                />
                <label
                    htmlFor={userAgreementCheckboxId}
                    className={styles.userAgreementCheckboxLabel}
                >
                    {userAgreementText1}{' '}
                    <Link
                        route={makeInfoPageRoute('eula')}
                        target="_blank"
                        className={styles.labelLink}
                    >
                        {userAgreementText2}
                    </Link>
                </label>

                <Button
                    isLoading={isFormSubmitting || isCaptchaLoading}
                    type="submit"
                    variant="green"
                    isFullWith
                    disabled={selectedWholesale === null}
                    className={styles.button}
                >
                    Продолжить
                </Button>
                {isGeneralErrorShowing && (
                    <p className={styles.commonError}>
                        {applyTypograf(
                            'Неправильно введённый пароль или выбранное юридическое лицо. Проверьте данные и попробуйте снова.',
                        )}
                    </p>
                )}
                <InvisibleCaptcha className={styles.captcha} {...invisibleCaptcha} />

                {phone !== null && selectedWholesale !== null && (
                    <p className={styles.forgottenPassword}>
                        Забыли пароль? Обратитесь в&nbsp;
                        <a
                            href={`tel:${formatRussianPhoneNumber(phone)}`}
                            className={styles.support}
                        >
                            техподдержку
                        </a>
                    </p>
                )}
            </form>
        </div>
    );
};
